/**
 * Refresh-token flow — bare Axios calls (no apiClient interceptors) to avoid retry loops.
 * Used by apiClient interceptors and authService.
 */
import axios, { type AxiosResponse } from 'axios';
import { eventBus } from '../../platform/eventBus';
import { API_BASE_URL } from '../../config/env';
import { API_ENDPOINTS } from '../endpoints';
import { mapRefreshTokenRequest } from './authMapper';
import type { RefreshTokenRequest, RefreshTokenResponse } from './authTypes';
import { API_TIMEOUT, STORAGE_KEYS } from '../../constants';
import { storage } from '../../platform/storage';
import { getDeviceId, getDeviceTypeCode } from '../../platform/device';
import { signalSessionExpired, isAuthSessionFailure } from './sessionExpired';
import { getApiErrorMessage } from '../../utils/apiErrorHandler';
import type { ApiError } from '../../types';
import { clearUserSessionData } from './clearUserSession';

export const AUTH_EVENTS = {
  TOKEN_REFRESHED: 'AUTH_TOKEN_REFRESHED',
  /** @deprecated Use SESSION_EXPIRED — handled in AuthContext with modal UX */
  FORCE_LOGOUT: 'FORCE_LOGOUT',
  SESSION_EXPIRED: 'AUTH_SESSION_EXPIRED',
  REFRESH_FAILED_RECOVERABLE: 'AUTH_REFRESH_FAILED_RECOVERABLE',
  /** User-specific storage wiped — Context providers should reset in-memory state. */
  USER_SESSION_CLEARED: 'AUTH_USER_SESSION_CLEARED',
} as const;

interface RecoverableRefreshFailurePayload {
  message: string;
  translationKey: 'auth.sessionRefreshFailed.message';
}

const REFRESH_BUFFER_MS = 30_000;
const BASE_URL = API_BASE_URL;

/** Auth endpoints that must not trigger refresh / retry on 401. */
export const AUTH_NO_REFRESH_PATHS: readonly string[] = [
  API_ENDPOINTS.AUTH.LOGIN,
  API_ENDPOINTS.AUTH.REFRESH_TOKEN,
  API_ENDPOINTS.AUTH.REVOKE_TOKEN,
  API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
  API_ENDPOINTS.AUTH.VERIFY_PASSWORD_RESET_OTP,
  API_ENDPOINTS.AUTH.VALIDATE_PASSWORD_RESET_TOKEN,
  API_ENDPOINTS.AUTH.RESET_PASSWORD,
];

export const shouldAttemptTokenRefresh = (url?: string): boolean => {
  if (!url) {
    return true;
  }
  return !AUTH_NO_REFRESH_PATHS.some(path => url.includes(path));
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const decodeBase64Url = (input: string): string => {
  const padded = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  const b64 = padded + pad;
  const atobFn = (globalThis as typeof globalThis & { atob?: (data: string) => string }).atob;
  if (typeof atobFn !== 'function') {
    return '';
  }
  return atobFn(b64);
};

/** JWT `exp` (seconds) converted to epoch milliseconds, or null if missing. */
export const getJwtExpiryMs = (token: string | null | undefined): number | null => {
  if (!token) {
    return null;
  }
  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) {
      return null;
    }
    const payload = asRecord(JSON.parse(decodeBase64Url(payloadPart)));
    const exp = payload?.exp;
    const seconds = typeof exp === 'number' ? exp : typeof exp === 'string' ? Number(exp) : NaN;
    if (!Number.isFinite(seconds) || seconds <= 0) {
      return null;
    }
    return seconds * 1000;
  } catch {
    return null;
  }
};

/**
 * Parses stored `expiresAt` values: ISO strings (timezone-optional treated as UTC),
 * unix seconds, or unix milliseconds.
 */
export const parseExpiryMs = (
  expiresAt: string | number | null | undefined,
): number | null => {
  if (expiresAt == null || expiresAt === '') {
    return null;
  }
  if (typeof expiresAt === 'number' && Number.isFinite(expiresAt)) {
    return expiresAt < 1_000_000_000_000 ? expiresAt * 1000 : expiresAt;
  }
  const raw = String(expiresAt).trim();
  if (!raw) {
    return null;
  }
  if (/^\d+(\.\d+)?$/.test(raw)) {
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) {
      return null;
    }
    return numeric < 1_000_000_000_000 ? numeric * 1000 : numeric;
  }
  const hasZone = /Z$/i.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw);
  const ms = Date.parse(hasZone ? raw : `${raw}Z`);
  return Number.isNaN(ms) ? null : ms;
};

export const isTokenExpired = (
  expiresAt: string | null | undefined,
  bufferMs: number = REFRESH_BUFFER_MS,
): boolean => {
  const expiryTime = parseExpiryMs(expiresAt);
  if (expiryTime == null) {
    return false;
  }
  return expiryTime - bufferMs <= Date.now();
};

/**
 * True when the access token is expired or inside the refresh buffer.
 * Uses the earlier of stored `expiresAt` and the JWT `exp` claim.
 */
export const isAccessTokenExpired = (
  accessToken: string | null | undefined,
  expiresAt?: string | number | null,
  bufferMs: number = REFRESH_BUFFER_MS,
): boolean => {
  const candidates: number[] = [];
  const stored = parseExpiryMs(expiresAt);
  if (stored != null) {
    candidates.push(stored);
  }
  const jwtExpiry = getJwtExpiryMs(accessToken);
  if (jwtExpiry != null) {
    candidates.push(jwtExpiry);
  }
  if (candidates.length === 0) {
    return false;
  }
  return Math.min(...candidates) - bufferMs <= Date.now();
};

const readTokenSet = (value: unknown): RefreshTokenResponse | null => {
  const obj = asRecord(value);
  if (!obj) {
    return null;
  }
  const accessToken = obj.accessToken ?? obj.AccessToken;
  if (typeof accessToken !== 'string' || !accessToken) {
    return null;
  }
  const refreshToken = obj.refreshToken ?? obj.RefreshToken;
  const expiresAt = obj.expiresAt ?? obj.ExpiresAt;
  const tokenType = obj.tokenType ?? obj.TokenType;
  return {
    accessToken,
    refreshToken: typeof refreshToken === 'string' ? refreshToken : '',
    expiresAt:
      typeof expiresAt === 'string'
        ? expiresAt
        : typeof expiresAt === 'number'
          ? String(expiresAt)
          : '',
    tokenType: typeof tokenType === 'string' ? tokenType : 'Bearer',
  };
};

/** Accepts a flat token set or common wrappers (`tokens`, `data`, `data.tokens`). */
export const unwrapAuthTokenSet = (payload: unknown): RefreshTokenResponse | null => {
  const direct = readTokenSet(payload);
  if (direct) {
    return direct;
  }
  const root = asRecord(payload);
  if (!root) {
    return null;
  }
  return (
    readTokenSet(root.tokens) ||
    readTokenSet(root.data) ||
    readTokenSet(asRecord(root.data)?.tokens) ||
    readTokenSet(root.result) ||
    null
  );
};

export const persistAuthTokens = async (
  tokens: RefreshTokenResponse,
): Promise<string> => {
  await storage.setItem(STORAGE_KEYS.AUTH_TOKEN, tokens.accessToken);

  if (tokens.refreshToken) {
    await storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
  }

  let expiresAt = tokens.expiresAt;
  if (!expiresAt) {
    const jwtExpiry = getJwtExpiryMs(tokens.accessToken);
    if (jwtExpiry != null) {
      expiresAt = new Date(jwtExpiry).toISOString();
    }
  }

  if (expiresAt) {
    await storage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, expiresAt);
  }

  eventBus.emit(AUTH_EVENTS.TOKEN_REFRESHED, {
    token: tokens.accessToken,
  });

  return tokens.accessToken;
};

export const clearAuthSession = async (): Promise<void> => {
  await clearUserSessionData({ includeAuthTokens: true });
};

/**
 * Low-level RefreshToken API call — uses bare Axios (not apiClient).
 */
export const postRefreshToken = (
  payload: RefreshTokenRequest,
): Promise<AxiosResponse<RefreshTokenResponse>> =>
  axios.post<RefreshTokenResponse>(
    `${BASE_URL}${API_ENDPOINTS.AUTH.REFRESH_TOKEN}`,
    payload,
    {
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    },
  );

let refreshPromise: Promise<string> | null = null;
let recoverableRefreshFailureNotified = false;

const executeRefresh = async (): Promise<string> => {
  const storedRefreshToken = await storage.getItem<string>(STORAGE_KEYS.REFRESH_TOKEN);
  const storedAccessToken = await storage.getItem<string>(STORAGE_KEYS.AUTH_TOKEN);

  if (!storedRefreshToken && !storedAccessToken) {
    throw new Error('Not logged in');
  }

  if (!storedRefreshToken) {
    throw new Error('No refresh token available');
  }

  const deviceId = await getDeviceId();
  const request = mapRefreshTokenRequest(
    storedRefreshToken,
    deviceId,
    getDeviceTypeCode(),
  );
  const { data } = await postRefreshToken(request);
  const tokens = unwrapAuthTokenSet(data);

  if (tokens?.accessToken) {
    const token = await persistAuthTokens(tokens);
    recoverableRefreshFailureNotified = false;
    return token;
  }

  const envelope = asRecord(data);
  if (envelope && envelope.success === false) {
    const status =
      typeof envelope.statusCode === 'number' ? envelope.statusCode : 400;
    const message =
      typeof envelope.message === 'string' && envelope.message
        ? envelope.message
        : 'Refresh failed';
    const wrapped = Object.assign(new Error(message), {
      response: { status, data: envelope },
    });
    throw wrapped;
  }

  throw new Error('RefreshToken endpoint did not return a token');
};

const executeRefreshWithRetry = async (retries = 2, delayMs = 1000): Promise<string> => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await executeRefresh();
    } catch (error) {
      if (isAuthSessionFailure(error) || attempt === retries) {
        throw error;
      }
      await new Promise(resolve => setTimeout(() => resolve(undefined), delayMs));
    }
  }
  throw new Error('Unreachable');
};

/**
 * Calls POST /api/v1/Auth/RefreshToken, persists tokens, and notifies AuthContext.
 * Concurrent callers share one in-flight refresh.
 */
export const refreshAccessToken = async (): Promise<string> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = executeRefreshWithRetry()
    .catch(async error => {
      if (isAuthSessionFailure(error)) {
        await clearAuthSession();
        signalSessionExpired();
      } else if (!recoverableRefreshFailureNotified) {
        recoverableRefreshFailureNotified = true;
        const message = getApiErrorMessage(error as ApiError);
        const payload: RecoverableRefreshFailurePayload = {
          message,
          translationKey: 'auth.sessionRefreshFailed.message',
        };
        eventBus.emit(AUTH_EVENTS.REFRESH_FAILED_RECOVERABLE, payload);
      }
      throw error;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

interface EnsureValidAccessTokenOptions {
  forceRefresh?: boolean;
}

/**
 * Returns a valid access token, refreshing proactively when expired.
 * Does not show session-expired UI — only refreshAccessToken does on hard auth failure.
 */
export const ensureValidAccessToken = async (
  options: EnsureValidAccessTokenOptions = {},
): Promise<string | null> => {
  const accessToken = await storage.getItem<string>(STORAGE_KEYS.AUTH_TOKEN);
  const refreshToken = await storage.getItem<string>(STORAGE_KEYS.REFRESH_TOKEN);
  const expiresAt = await storage.getItem<string>(STORAGE_KEYS.TOKEN_EXPIRES_AT);

  if (!accessToken && !refreshToken) {
    return null;
  }

  const needsRefresh =
    options.forceRefresh ||
    !accessToken ||
    (Boolean(refreshToken) && isAccessTokenExpired(accessToken, expiresAt));

  if (!needsRefresh) {
    return accessToken;
  }

  if (!refreshToken) {
    return accessToken ?? null;
  }

  return refreshAccessToken();
};

/** Test helper — resets in-flight refresh state between unit tests. */
export const resetTokenRefreshState = (): void => {
  refreshPromise = null;
  recoverableRefreshFailureNotified = false;
};
